<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("barcode_print") }}</h4>
        </div>
      </div>

      <div class="page-btn d-flex gap-2 align-items-center flex-wrap">
        <select v-model="printType" class="form-select form-select-sm barcode-print-type">
          <option v-for="layout in printLayouts" :key="layout.id" :value="layout.id">
            {{ layout.label }}
          </option>
        </select>
        <button
          type="button"
          class="btn btn-added"
          :disabled="!addedItems.length || printing"
          @click="printBarcodes"
        >
          <span v-if="printing" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="ti ti-printer me-1"></i>{{ $t('print_preview') }}
        </button>
      </div>
    </div>

    <div class="card table-list-card">
      <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
        <div class="flex-grow-1 me-3 position-relative" style="max-width: 560px;">
          <div class="position-relative">
            <input
              ref="itemSearchInput"
              type="text"
              class="form-control"
              v-model="query"
              placeholder="Search Items by Name, Code, SKU or Barcode"
              autocomplete="off"
              @input="onSearchInput"
              @keydown="handleKeys"
            />
            <div
              v-if="query"
              class="search-addon position-absolute top-50 end-0 translate-middle-y me-3"
              style="cursor: pointer;"
            >
              <span @click="clearSearch">
                <i class="fa fa-times"></i>
              </span>
            </div>
          </div>

          <!-- Same dropdown layout as POS item search -->
          <div
            v-if="query"
            class="search-results shadow rounded bg-white position-absolute w-100 mt-2 d-block"
            style="max-height: 450px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
          >
            <ul v-if="results.length" class="list-group list-group-flush mb-0">
              <li
                v-for="(item, index) in results"
                :key="resultKey(item)"
                class="list-group-item"
                :class="{ 'bg-primary text-white': index === itemIndex }"
                style="padding: 10px; cursor: pointer;"
                @click="selectItem(item)"
              >
                <div class="d-flex align-items-center gap-2">
                  <div
                    style="width:50px; height:50px; border-radius:4px; overflow:hidden; flex-shrink:0;"
                  >
                    <img
                      :src="$getRowImageUrl(item)"
                      alt="Item"
                      style="width:100%; height:100%; object-fit:cover;"
                    />
                  </div>

                  <div class="d-flex flex-column">
                    <strong :class="index === itemIndex ? 'text-white' : 'text-dark'">
                      {{ item.item_name }} - {{ item.item_code }}
                    </strong>
                    <span style="font-size:0.75rem;" :class="index === itemIndex ? 'text-white' : ''">
                      Stock: {{ getAvailableStock(item) }}
                    </span>
                    <span
                      v-if="!isVariantParent(item)"
                      style="font-size:0.75rem;"
                      :class="index === itemIndex ? 'text-white' : ''"
                    >
                      {{ $t('selling_price') }}: {{ formatMoney(item.selling_price ?? 0) }}
                    </span>
                    <template v-if="item.item_type_code === 'MULTI_VARIANT'">
                      <span
                        v-if="item.sku"
                        style="font-size:0.75rem;"
                        :class="index === itemIndex ? 'text-white' : ''"
                      >
                        SKU: {{ item.sku }}
                      </span>
                      <span
                        v-if="item.barcode_qr_code"
                        style="font-size:0.75rem;"
                        :class="index === itemIndex ? 'text-white' : ''"
                      >
                        Barcode: {{ item.barcode_qr_code }}
                      </span>
                      <span
                        v-if="item.variant_attributes?.length"
                        style="font-size:0.75rem;"
                        :class="index === itemIndex ? 'text-white' : ''"
                      >
                        {{ formatAttributes(item.variant_attributes) }}
                      </span>
                    </template>
                  </div>
                </div>
              </li>
            </ul>

            <div v-else class="text-center text-muted py-4">
              {{ searching ? 'Searching…' : 'No results found' }}
            </div>
          </div>
        </div>
        <button type="button" class="btn btn-white btn-md" :disabled="!addedItems.length" @click="clearItems">
          <i class="ti ti-trash me-1"></i>{{ $t('cancel') }}
        </button>
      </div>

      <div class="card-body p-0">
        <div class="table-responsive">
          <div class="table-wrapper px-3 pb-3">
            <table class="table datatable align-middle mb-0">
              <thead class="thead-light">
                <tr>
                  <th>{{ $t('item_code') }}</th>
                  <th>{{ $t('item_name') }}</th>
                  <th>{{ $t('barcode_qr_code') }}</th>
                  <th>{{ $t('stock_quantity') }}</th>
                  <th style="width: 8rem">{{ $t('print_qty') }}</th>
                  <th class="no-sort text-end">{{ $t('action') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in addedItems" :key="rowKey(row)">
                  <td>{{ row.item_code }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <span class="avatar avatar-md me-2">
                        <img :src="$getRowImageUrl(row)" alt="Item" />
                      </span>
                      <span>
                        {{ row.item_name }}
                        <small v-if="row.variant_label" class="d-block text-muted">{{ row.variant_label }}</small>
                      </span>
                    </div>
                  </td>
                  <td>{{ row.barcode || '—' }}</td>
                  <td>{{ row.stock_quantity }}</td>
                  <td>
                    <input
                      type="number"
                      class="form-control form-control-sm"
                      v-model.number="row.print_qty"
                      min="1"
                      max="100"
                    />
                  </td>
                  <td class="action-table-data text-end">
                    <div class="edit-delete-action justify-content-end">
                      <a
                        href="javascript:void(0);"
                        class="p-2"
                        title="Remove"
                        @click.prevent="removeItem(index)"
                      >
                        <i class="ti ti-trash"></i>
                      </a>
                    </div>
                  </td>
                </tr>
                <tr v-if="!addedItems.length">
                  <td colspan="6" class="text-center text-muted py-4">
                    Search and add items or variants to print barcodes
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import apiUrls from '@/urls';
import { useToast } from 'vue-toastification';
import { truncateMoney2 } from '@/utils';

export default {
  name: 'BarcodeItems',

  setup() {
    return { toast: useToast() };
  },

  data() {
    return {
      // Ascending by label size; 50×30 mm is the recommended default.
      printLayouts: [
        { id: '40x30', label: '40 × 30 mm' },
        { id: '50x25', label: '50 × 25 mm' },
        { id: '50x30', label: '50 × 30 mm' },
        { id: '80x40', label: '80 × 40 mm' },
        { id: 'a4', label: 'A4' },
      ],
      printType: '50x30',
      query: '',
      results: [],
      itemIndex: 0,
      addedItems: [],
      printing: false,
      searching: false,
      debouncedSearch: null,
    };
  },

  created() {
    this.debouncedSearch = debounce(this.searchItems, 300);
  },

  mounted() {
    const saved = localStorage.getItem('printType');
    const known = this.printLayouts.some((layout) => layout.id === saved);
    // Migrate old values (a4 sticker default / thermal) to the recommended 50×30 mm.
    this.printType = known ? saved : '50x30';
  },

  beforeUnmount() {
    this.debouncedSearch?.cancel?.();
  },

  watch: {
    printType(val) {
      localStorage.setItem('printType', val);
    },
    addedItems: {
      deep: true,
      handler(rows) {
        rows.forEach((row) => {
          if (!Number.isFinite(row.print_qty) || row.print_qty < 1) row.print_qty = 1;
          if (row.print_qty > 100) row.print_qty = 100;
        });
      },
    },
  },

  methods: {
    formatMoney(amount) {
      return truncateMoney2(amount);
    },

    formatAttributes(attrs) {
      return (attrs || []).map((v) => `${v.attribute}: ${v.value}`).join(' | ');
    },

    isVariantParent(item) {
      return item?.item_type_code === 'MULTI_VARIANT' && !item?.variant_id;
    },

    getAvailableStock(item) {
      return Number(item.current_stock ?? item.stock_qty ?? item.stock_quantity ?? 0);
    },

    resultKey(item) {
      return item.variant_id ? `v${item.variant_id}` : `i${item.id || item.item_id}`;
    },

    rowKey(row) {
      return row.variant_id ? `v${row.variant_id}` : `i${row.item_id}`;
    },

    onSearchInput() {
      this.debouncedSearch();
    },

    clearSearch() {
      this.query = '';
      this.results = [];
      this.itemIndex = 0;
      this.$nextTick(() => this.$refs.itemSearchInput?.focus());
    },

    async searchItems() {
      const q = (this.query || '').trim();
      if (!q) {
        this.results = [];
        this.searching = false;
        return;
      }
      this.searching = true;
      try {
        const { data } = await this.$axios.get(apiUrls.item_search.get, { params: { q } });
        const results = data.results || [];
        // Default print qty on each row (like POS quantity field).
        this.results = results.map((row) => ({
          ...row,
          quantity: row.quantity > 0 ? row.quantity : 1,
        }));
        this.itemIndex = 0;
      } catch (error) {
        this.results = [];
      } finally {
        this.searching = false;
      }
    },

    async loadVariants(itemId) {
      const { data } = await this.$axios.get(
        apiUrls.item.variants.replace(':item_id', itemId),
      );
      this.results = (data.results || []).map((row) => ({
        ...row,
        quantity: 1,
      }));
      this.itemIndex = 0;
      if (!this.results.length) {
        this.toast.warning('No variants found for this item');
      }
    },

    async selectItem(item) {
      if (this.isVariantParent(item)) {
        this.query = item.item_name || this.query;
        try {
          await this.loadVariants(item.id || item.item_id);
        } catch (error) {
          this.toast.error('Failed to load variants');
        }
        return;
      }

      const barcode = item.barcode_qr_code || item.barcode || item.item_code || '';
      const variantLabel = this.formatAttributes(item.variant_attributes);
      const key = this.resultKey(item);
      if (this.addedItems.some((row) => this.rowKey(row) === key)) {
        this.toast.warning('This item is already added!');
        return;
      }

      if (!barcode) {
        this.toast.warning('This item has no barcode to print');
        return;
      }

      this.addedItems.push({
        item_id: item.id || item.item_id,
        variant_id: item.variant_id || null,
        item_name: item.item_name,
        item_code: item.item_code,
        variant_label: variantLabel,
        item_price: item.selling_price,
        barcode,
        stock_quantity: this.getAvailableStock(item),
        print_qty: 1,
        // Keep image fields so $getRowImageUrl works in the table (same as POS).
        images: item.images || [],
        image: item.image || null,
        small_thumb: item.small_thumb || null,
      });

      this.results = [];
      this.itemIndex = 0;
      this.query = '';
    },

    handleKeys(event) {
      if (event.key === 'ArrowDown') {
        if (!this.results.length) return;
        this.itemIndex = Math.min(this.itemIndex + 1, this.results.length - 1);
        event.preventDefault();
      } else if (event.key === 'ArrowUp') {
        if (!this.results.length) return;
        this.itemIndex = Math.max(this.itemIndex - 1, 0);
        event.preventDefault();
      } else if (event.key === 'Enter') {
        const selected = this.results[this.itemIndex];
        if (selected) this.selectItem(selected);
        event.preventDefault();
      } else if (event.key === 'Escape') {
        this.clearSearch();
      }
    },

    removeItem(index) {
      this.addedItems.splice(index, 1);
    },

    clearItems() {
      this.addedItems = [];
      this.clearSearch();
    },

    async printBarcodes() {
      if (!this.addedItems.length) {
        this.toast.warning('No items to print');
        return;
      }

      const invalid = this.addedItems.some(
        (item) => !item.print_qty || item.print_qty < 1 || item.print_qty > 100,
      );
      if (invalid) {
        this.toast.warning('Print quantity must be between 1 and 100');
        return;
      }

      const payload = {
        print_type: this.printType,
        items: this.addedItems.map((item) => ({
          // Backend encodes this value as the barcode image + number line.
          item_code: item.barcode || item.item_code,
          item_name: item.item_name,
          variant_label: item.variant_label || '',
          qty: item.print_qty,
          item_price: item.item_price,
        })),
      };

      this.printing = true;
      try {
        const res = await this.$axios.post(apiUrls.print_barcodes.post, payload, {
          responseType: 'text',
        });
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          this.toast.warning('Allow pop-ups to preview barcodes');
          return;
        }
        // Template triggers print and closes the tab after Print or Cancel.
        printWindow.document.write(res.data);
        printWindow.document.close();
      } catch (error) {
        this.toast.error('Failed to print barcodes');
      } finally {
        this.printing = false;
      }
    },
  },
};
</script>

<style scoped>
.barcode-print-type {
  width: 12rem;
  min-height: 38px;
}

.search-results .list-group-item.bg-primary strong,
.search-results .list-group-item.bg-primary span {
  color: #fff !important;
}
</style>
